/**
 * AdminSupportPage - tickets de clientes
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Send, Loader2 } from "lucide-react";
import { api, getApiErrorMessage } from "@/lib/api";
import StatusBadge from "@/components/shared/StatusBadge";
import { fmtDateTime } from "@/lib/date";
import { toast } from "sonner";

export default function AdminSupportPage() {
    const [tab, setTab] = useState("all");
    const [items, setItems] = useState([]);
    const [sel, setSel] = useState(null);
    const [reply, setReply] = useState("");
    const [statusUpd, setStatusUpd] = useState("");
    const [working, setWorking] = useState(false);

    const load = async () => {
        const status = tab === "all" ? "" : tab;
        const { data } = await api.get(`/admin/support${status ? `?status=${status}` : ""}`);
        setItems(data);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [tab]);

    const send = async () => {
        if (!sel || !reply) return;
        setWorking(true);
        try {
            await api.post(`/admin/support/${sel.id}/reply`, {
                message: reply,
                status: statusUpd || undefined,
            });
            toast.success("Resposta enviada");
            setReply("");
            setStatusUpd("");
            const fresh = await api.get("/admin/support");
            setItems(fresh.data);
            setSel(fresh.data.find((t) => t.id === sel.id));
        } catch (e) {
            toast.error(getApiErrorMessage(e));
        } finally {
            setWorking(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-support-page">
            <div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Suporte</h1>
                <p className="text-muted-foreground">Pedidos de ajuda dos clientes.</p>
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="all">Todos</TabsTrigger>
                    <TabsTrigger value="aberto">Abertos</TabsTrigger>
                    <TabsTrigger value="em_curso">Em curso</TabsTrigger>
                    <TabsTrigger value="resolvido">Resolvidos</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4 space-y-3">
                    {items.map((t) => (
                        <Card
                            key={t.id}
                            className="glass-card p-4 rounded-2xl border-white/10 cursor-pointer hover:border-[#6B21D9]/40"
                            onClick={() => setSel(t)}
                            data-testid={`admin-ticket-${t.id}`}
                        >
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <StatusBadge status={t.status} />
                                        <h4 className="font-medium">{t.subject}</h4>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                        {t.user_name} · {t.user_email} · prioridade {t.priority}
                                    </div>
                                </div>
                                <span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span>
                            </div>
                            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{t.message}</p>
                        </Card>
                    ))}
                    {items.length === 0 && (
                        <Card className="p-8 text-center text-muted-foreground">Sem tickets.</Card>
                    )}
                </TabsContent>
            </Tabs>

            <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {sel && <StatusBadge status={sel.status} />}
                            <span>{sel?.subject}</span>
                        </DialogTitle>
                    </DialogHeader>
                    {sel && (
                        <div className="space-y-3">
                            <Card className="p-3 rounded-xl bg-secondary/40">
                                <div className="text-xs text-muted-foreground mb-1">{sel.user_name} · {fmtDateTime(sel.created_at)}</div>
                                <p className="text-sm whitespace-pre-line">{sel.message}</p>
                            </Card>
                            {(sel.replies || []).map((r, i) => (
                                <Card key={i} className={`p-3 rounded-xl ${r.from === "admin" ? "bg-[#6B21D9]/10 border-[#6B21D9]/30" : "bg-secondary/40"}`}>
                                    <div className="text-xs text-muted-foreground mb-1">
                                        {r.from === "admin" ? "Equipa MeSafa" : sel.user_name} · {fmtDateTime(r.at)}
                                    </div>
                                    <p className="text-sm whitespace-pre-line">{r.message}</p>
                                </Card>
                            ))}
                            <div className="space-y-2 pt-2">
                                <Label>Resposta</Label>
                                <Textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-reply-input" />
                                <div className="flex items-center gap-2">
                                    <Select value={statusUpd} onValueChange={setStatusUpd}>
                                        <SelectTrigger className="w-44"><SelectValue placeholder="Mudar estado..." /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="em_curso">Em curso</SelectItem>
                                            <SelectItem value="resolvido">Resolvido</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button onClick={send} disabled={working || !reply} className="btn-grad-purple text-white" data-testid="admin-send-reply">
                                        {working ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                        Enviar
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
